import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError } from "@/api";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { SectionError, SectionLoading } from "@/components/section";
import { NotFoundPage } from "@/features/errors/NotFoundPage";
import { useSpaceInSidebar } from "@/features/shell/useSidebarSpaces";
import { ActivitySection } from "./ActivitySection";
import { AgentsSection } from "./AgentsSection";
import { SpaceAsk } from "./ask/SpaceAsk";
import { PinnedSection } from "./PinnedSection";
import { RecentChatsSection } from "./RecentChatsSection";
import { SharedSection } from "./SharedSection";
import { SpaceAboutRail } from "./SpaceAboutRail";
import { SpaceMembersDialog } from "./SpaceDialogs";
import { SpaceHeader } from "./SpaceHeader";
import { useSpaceHub } from "./useSpaceHub";
import { WorkflowsSection } from "./WorkflowsSection";

/** `/spaces/:spaceUuid`: the space's overview (shared and private spaces). */
export function SpaceDetailPage() {
  const { spaceUuid = "" } = useParams();
  const hub = useSpaceHub(spaceUuid);
  const areas = useAreaCrumbs();
  const inSidebar = useSpaceInSidebar(spaceUuid);
  const [membersOpen, setMembersOpen] = useState(false);
  const navigate = useNavigate();

  // 403 too: a space I can't open looks the same as one that doesn't exist.
  if (hub.error instanceof ApiError && (hub.error.status === 404 || hub.error.status === 403)) {
    return <NotFoundPage />;
  }

  const frame = "mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 md:px-8 md:py-10";
  if (hub.isPending) {
    return (
      <div className={frame}>
        <SectionLoading rows={6} />
      </div>
    );
  }
  if (!hub.data) {
    return (
      <div className={frame}>
        <SectionError onRetry={() => void hub.refetch()} error={hub.error} />
      </div>
    );
  }

  const space = hub.data;
  return (
    <>
      {inSidebar === false && <PageBreadcrumbs trail={[areas.spaces]} current={space.name} />}
      <div className={frame}>
        <SpaceHeader
          space={space}
          onOpenMembers={() =>
            hub.data?.canEdit ? navigate(`/spaces/${spaceUuid}/settings/members`) : setMembersOpen(true)
          }
          onOpenSettings={() => navigate(`/spaces/${spaceUuid}/settings`)}
        />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="flex flex-col gap-7 lg:col-span-2">
            <SpaceAsk spaceUuid={space.uuid} isPersonal={Boolean(space.isPersonal)} />
            <RecentChatsSection space={space} />
            <SharedSection spaceUuid={space.uuid} />
            <PinnedSection space={space} />
            <AgentsSection space={space} />
            <WorkflowsSection space={space} />
            <ActivitySection space={space} />
          </div>
          <SpaceAboutRail space={space} />
        </div>
        {membersOpen && <SpaceMembersDialog space={space} onClose={() => setMembersOpen(false)} />}
      </div>
    </>
  );
}
